import React from 'react';
import { Button, Container, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import API, { useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { CircleLoader } from '../loader';
import { ValidatableField } from '../form';
const msgs = defineMessages({
    errorNoEmail: {
        id: 'pages.guest.EmailUnverifiedPage.error.noEmail',
        defaultMessage: 'email is null',
    },
    headerTitle: {
        id: 'pages.guest.EmailUnverifiedPage.header.title',
        defaultMessage: 'Email Unverified',
    },
    buttonBackToLoginPage: {
        id: 'pages.guest.EmailUnverifiedPage.button.backToLoginPage',
        defaultMessage: 'Back to the login page',
    },
    buttonCancel: {
        id: 'pages.guest.EmailUnverifiedPage.button.cancel',
        defaultMessage: 'Cancel',
    },
    buttonResendEmail: {
        id: 'pages.guest.EmailUnverifiedPage.button.resendEmail',
        defaultMessage: 'Resend email',
    },
    inputEmail: {
        id: 'pages.guest.EmailUnverifiedPage.input.email',
        defaultMessage: 'Email',
    },
    textPreSentMessage: {
        id: 'pages.guest.EmailUnverifiedPage.text.preSentMessage',
        defaultMessage: 'Your email is not verified yet. Please follow the link in the verification email. ' +
            'To resend the verification email, please enter the email.',
    },
    textPostSentMessage: {
        id: 'pages.guest.EmailUnverifiedPage.text.postSentMessage',
        defaultMessage: 'An email was sent out. Please follow the instruction in the email to verify your email.',
    },
});
function EmailUnverifiedPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { showSuccess } = useSnackbar();
    const { register, formState, handleSubmit: validate } = useForm();
    const { errors } = formState;
    const { signOut } = useAuth();
    const { csrfToken } = useCsrfToken();
    const [email, setEmail] = React.useState();
    const handleGoBack = async () => {
        await signOut?.();
        navigate('/');
    };
    const { mutate, isSuccess, isLoading } = useMutation(() => {
        if (!email) {
            return Promise.reject(intl.formatMessage(msgs.errorNoEmail));
        }
        return API.Accounts.resendEmail(csrfToken, email);
    }, {
        onSuccess: () => {
            showSuccess('Email sent');
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleSubmit = () => mutate();
    return (React.createElement(Container, { component: "main", sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { variant: "h6", align: "center" }, intl.formatMessage(msgs.headerTitle)),
                    React.createElement(Typography, { variant: "body1" }, intl.formatMessage(isSuccess ? msgs.textPostSentMessage : msgs.textPreSentMessage)),
                    isLoading ? React.createElement(CircleLoader, null) : null,
                    isSuccess ? (React.createElement(Button, { onClick: handleGoBack, startIcon: React.createElement(ArrowBackIcon, null), size: "medium" }, intl.formatMessage(msgs.buttonBackToLoginPage))) : (React.createElement(React.Fragment, null,
                        React.createElement(ValidatableField, { id: "email", type: "email", label: intl.formatMessage(msgs.inputEmail), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                pattern: /\S+@\S+\.\S+/,
                                maxLength: 128,
                                onChange: (e) => setEmail(e.target.value),
                            } }),
                        React.createElement(Stack, { direction: "row", gap: 1 },
                            React.createElement(Button, { disabled: isLoading, fullWidth: true, onClick: handleGoBack, size: "medium" }, intl.formatMessage(msgs.buttonCancel)),
                            React.createElement(Button, { color: "primary", disabled: isLoading || !email, fullWidth: true, type: "submit", size: "medium" }, intl.formatMessage(msgs.buttonResendEmail))))))))));
}
export default React.memo(EmailUnverifiedPage);
